/**
 * HammerSkillEffect — Earth tree stone_fist evolution (stone_fist_evolved).
 *
 * Replaces Stone Fist. Both arms encase in stone over 0.8 seconds of highly
 * visible wind-up. Overhead slam — highest single-target damage in the Earth tree.
 *
 * Phased skill: wind-up (800ms, uncancellable) → active (hit + zone creation) →
 * recovery → cooldown (medium, 800ms).
 *
 * On hit:
 * - Single melee hit on the closest enemy in facing direction
 * - Guaranteed knockback bypass: `ignoreKnockbackResistance: true` (baked)
 * - Creates a ZoneSystem rough_terrain zone at the impact point:
 *     type='rough_terrain', radiusTiles=1.5, durationMs=6000, speedMultiplier=0.6
 * - If Aftershock passive is unlocked (resolver.hasEffect('stone_fist_evolved', 'aftershockActive')):
 *     also creates a tremor zone at the same point
 *
 * Does NOT inherit Stone Fist upgrade keys (no cleave arc, no stationary bonus).
 * Stone Fist upgrade keys (cleaveArcDegrees, stationaryDamageBonus) belong to
 * the stone_fist skillId scope. Hammer is an independent skill node.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Stone Fist Path — Hammer
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface HammerDeps {
  readonly resolver: {
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly targeting: {
    /** Returns the closest enemy ID in the player's facing direction, or null if none. */
    getSingleTarget(): string | null;
    /** Returns the world-space position of the melee impact point. */
    getImpactPosition(): { x: number; y: number };
  };
  readonly combat: {
    applyDamage(
      targetId: string,
      damage: number,
      opts: { sourceSkillId: string; ignoreKnockbackResistance: boolean },
    ): void;
  };
  readonly zones: {
    create(config: {
      type: string;
      position: { x: number; y: number };
      radiusTiles: number;
      durationMs: number;
      speedMultiplier?: number;
      sourceSkillId: string;
    }): string;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface HammerConfig {
  readonly windupMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  /** Base damage — highest single-target hit in the Earth tree. */
  readonly baseDamage: number;
  /** Crater zone radius in tiles. */
  readonly craterRadiusTiles: number;
  /** Crater zone lifetime in ms. */
  readonly craterDurationMs: number;
  /** Speed multiplier inside the crater zone (0.6 = 60% speed). */
  readonly craterSpeedMultiplier: number;
  /** Tremor zone radius in tiles (when Aftershock is active). */
  readonly tremorRadiusTiles: number;
  /** Tremor zone lifetime in ms (when Aftershock is active). */
  readonly tremorDurationMs: number;
}

const DEFAULT_CONFIG: HammerConfig = {
  windupMs: 800,                 // longer wind-up than Stone Fist's 600ms
  recoveryMs: 150,
  cooldownMs: 800,               // medium cooldown tier
  baseDamage: 32,                // highest single-target damage in the tree
  craterRadiusTiles: 1.5,
  craterDurationMs: 6000,
  craterSpeedMultiplier: 0.6,
  tremorRadiusTiles: 0.5,        // small tremor zone ~1 tile
  tremorDurationMs: 3000,
};

// ---------------------------------------------------------------------------
// Phase sequence
// ---------------------------------------------------------------------------

const PHASE_SEQUENCE: readonly SkillPhase[] = ['windup', 'active', 'recovery', 'cooldown'];

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class HammerSkillEffect extends PhasedSkillEffect {
  readonly skillId = 'stone_fist_evolved';

  protected readonly phaseSequence = PHASE_SEQUENCE;

  private readonly deps: HammerDeps;
  private readonly config: HammerConfig;

  // Per-activation telemetry
  private _targetsHit: string[] = [];
  private _lastDamageDealt = 0;
  private _zonesCreated: string[] = [];
  private _aftershockZoneCreated = false;
  private _activationCount = 0;

  constructor(deps: HammerDeps, config: Partial<HammerConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get targetsHit(): readonly string[] { return this._targetsHit; }
  get lastDamageDealt(): number { return this._lastDamageDealt; }
  get zonesCreated(): readonly string[] { return this._zonesCreated; }
  get aftershockZoneCreated(): boolean { return this._aftershockZoneCreated; }
  get activationCount(): number { return this._activationCount; }

  // --- PhasedSkillEffect overrides ---

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return 16; // single frame
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  /** Wind-up and active phase are not interruptible — committing to the slam. */
  override get interruptible(): boolean {
    return this.phase !== 'windup' && this.phase !== 'active';
  }

  protected override onActivate(): void {
    this._targetsHit = [];
    this._lastDamageDealt = 0;
    this._zonesCreated = [];
    this._aftershockZoneCreated = false;
    this._activationCount++;
  }

  protected override onPhaseEnter(phase: SkillPhase): void {
    if (phase !== 'active') return;

    const target = this.deps.targeting.getSingleTarget();
    const impactPos = this.deps.targeting.getImpactPosition();

    // Apply melee hit — knockback resistance is unconditionally bypassed
    if (target !== null) {
      this.deps.combat.applyDamage(target, this.config.baseDamage, {
        sourceSkillId: 'stone_fist_evolved',
        ignoreKnockbackResistance: true,
      });
      this._targetsHit = [target];
      this._lastDamageDealt = this.config.baseDamage;
    }

    // Create rough terrain crater zone at impact point
    const craterZoneId = this.deps.zones.create({
      type: 'rough_terrain',
      position: impactPos,
      radiusTiles: this.config.craterRadiusTiles,
      durationMs: this.config.craterDurationMs,
      speedMultiplier: this.config.craterSpeedMultiplier,
      sourceSkillId: 'stone_fist_evolved',
    });
    this._zonesCreated.push(craterZoneId);

    // If Aftershock passive is unlocked, also place a tremor zone
    if (this.deps.resolver.hasEffect('stone_fist_evolved', 'aftershockActive')) {
      const tremorZoneId = this.deps.zones.create({
        type: 'tremor',
        position: impactPos,
        radiusTiles: this.config.tremorRadiusTiles,
        durationMs: this.config.tremorDurationMs,
        sourceSkillId: 'stone_fist_evolved',
      });
      this._zonesCreated.push(tremorZoneId);
      this._aftershockZoneCreated = true;
    }

    this.markHitApplied();
  }
}
